"use client"

import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useLocation } from "react-router-dom"
import { AnimatePresence, motion } from "framer-motion"
import { Calendar, MapPin, Users, ListChecks, FileText, Images, ArrowRight } from "lucide-react"

// Pembukaan: Jumat, 2 Okt 2026, 16:00 WITA. Acara berakhir: Minggu, 4 Okt 2026, malam.
const EVENT_START = new Date("2026-10-02T16:00:00+08:00")
const EVENT_END = new Date("2026-10-04T20:00:00+08:00")

type EventStatus = "upcoming" | "ongoing" | "ended"

interface TimeLeft {
  days: number
  hours: number
  minutes: number
  seconds: number
}

const getStatus = (now: Date): EventStatus => {
  if (now < EVENT_START) return "upcoming"
  if (now < EVENT_END) return "ongoing"
  return "ended"
}

const getTimeLeft = (now: Date): TimeLeft => {
  const diff = Math.max(0, EVENT_START.getTime() - now.getTime())
  const totalSeconds = Math.floor(diff / 1000)
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  }
}

const pad = (n: number) => String(n).padStart(2, "0")

// Kartu angka dengan efek terlipat (flap) dari atas ke bawah saat nilainya berubah
const FlipCard = ({ value, label }: { value: string; label: string }) => {
  const [prevValue, setPrevValue] = useState(value)
  const [flapValue, setFlapValue] = useState<string | null>(null)

  // Update sinkron saat render (derived state) untuk mencegah jeda visual (flicker)
  if (value !== prevValue) {
    setFlapValue(prevValue)
    setPrevValue(value)
  }

  // Kelas CSS yang diabstraksi agar tampilan lebih elegan dan menyatu dengan tema (tidak seperti mesin slot)
  const cardInnerClass = "flex items-center justify-center rounded-xl bg-gradient-to-b from-white to-slate-50/80 dark:from-slate-800 dark:to-slate-900/80 border border-slate-200/80 dark:border-slate-700/80 shadow-[0_4px_12px_-4px_rgba(30,58,138,0.1)]";
  const textClass = "text-4xl sm:text-5xl font-bold text-slate-800 dark:text-slate-100 tabular-nums leading-none tracking-tight";

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-full aspect-[4/3] [perspective:500px]">
        {/* Kartu dasar: selalu menampilkan angka terkini */}
        <div className={`absolute inset-0 ${cardInnerClass}`}>
          <span className={textClass}>
            {value}
          </span>
        </div>

        {/* Flap statis: bagian bawah angka lama, menutupi bagian bawah angka baru selama animasi */}
        {flapValue !== null && (
          <div className="absolute inset-x-0 bottom-0 h-1/2 z-10 overflow-hidden pointer-events-none rounded-b-xl">
            <div className={`absolute inset-x-0 bottom-0 h-[200%] ${cardInnerClass}`}>
              <span className={textClass}>
                {flapValue}
              </span>
            </div>
          </div>
        )}

        {/* Flap animasi: bagian atas angka lama, terlipat ke bawah menutupi flap statis */}
        <AnimatePresence>
          {flapValue !== null && (
            <motion.div
              initial={{ rotateX: 0 }}
              animate={{ rotateX: -90 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35, ease: "easeIn" }}
              onAnimationComplete={() => setFlapValue(null)}
              style={{ transformOrigin: "bottom", backfaceVisibility: "hidden" }}
              className="absolute inset-x-0 top-0 h-1/2 z-20 overflow-hidden pointer-events-none rounded-t-xl"
            >
              <div className={`absolute inset-x-0 top-0 h-[200%] ${cardInnerClass}`}>
                <span className={textClass}>
                  {flapValue}
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Garis engsel di tengah kartu (dibuat lebih halus menyerupai lipatan kertas) */}
        <div className="absolute left-0 right-0 top-1/2 h-px bg-slate-200/60 dark:bg-slate-700/60 z-30 pointer-events-none" />
      </div>
      <span className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 font-medium uppercase tracking-wider mt-2">
        {label}
      </span>
    </div>
  )
}

export default function EventCountdown() {
  const { i18n } = useTranslation()
  const isID = i18n.language === "id"
  const [now, setNow] = useState<Date>(() => new Date())
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(timer)
  }, [])

  const handleLinkClick = (href: string) => {
    // Tautan ke halaman lain (bukan hash di halaman yang sama)
    if (!href.startsWith("#")) {
      navigate(href)
      return
    }

    // Jika belum di halaman utama, arahkan ke root lalu scroll
    if (location.pathname !== "/") {
      navigate(`/${href}`)
      return
    }

    const target = document.querySelector(href)
    if (target) {
      target.scrollIntoView({ behavior: "smooth" })
      window.history.replaceState(null, "", href)
    }
  }

  const quickLinks = [
    { label: isID ? "Rundown Acara" : "Event Rundown", href: "#timeline", icon: ListChecks },
    { label: isID ? "Dokumen" : "Documents", href: "/documents", icon: FileText },
    { label: isID ? "Galeri" : "Gallery", href: "/gallery", icon: Images },
  ]

  const status = getStatus(now)
  const timeLeft = getTimeLeft(now)

  const units = [
    { label: isID ? "Hari" : "Days", value: timeLeft.days },
    { label: isID ? "Jam" : "Hours", value: timeLeft.hours },
    { label: isID ? "Menit" : "Minutes", value: timeLeft.minutes },
    { label: isID ? "Detik" : "Seconds", value: timeLeft.seconds },
  ]

  const statusText =
    status === "upcoming"
      ? isID ? "Menuju Hari Pelaksanaan" : "Counting Down to Opening"
      : status === "ongoing"
        ? isID ? "Sedang Berlangsung" : "Happening Now"
        : isID ? "Terima Kasih" : "Thank You"

  return (
    <section className="relative pt-10 pb-6 md:pt-14 md:pb-8 bg-white dark:bg-gray-900 overflow-hidden">
      {/* Background elegan, gradasi sangat halus menyatu dengan tema pendidikan, tanpa pattern dot yang terkesan 'techy' */}
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-blue-50/80 via-white to-white dark:from-blue-950/40 dark:via-gray-900 dark:to-gray-900 pointer-events-none" />

      <div className="container mx-auto px-4 max-w-4xl relative z-10">
        <div className="flex flex-col items-center text-center gap-4 sm:gap-5">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800">
            {status === "ongoing" && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />}
            <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
              {statusText}
            </span>
          </span>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white leading-snug px-2">
            {status === "ended"
              ? isID
                ? "Sampai jumpa di KONASPI XIII!"
                : "See you at KONASPI XIII!"
              : isID
                ? "Konvensi Nasional Pendidikan XII"
                : "XII National Education Convention"}
          </h2>

          {status === "upcoming" && (
            <div className="grid grid-cols-4 gap-2 sm:gap-4 w-full max-w-xs sm:max-w-lg mt-1">
              {units.map((u) => (
                <FlipCard key={u.label} value={pad(u.value)} label={u.label} />
              ))}
            </div>
          )}

          {status === "ongoing" && (
            <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base max-w-md px-2">
              {isID
                ? "Acara berlangsung 2-4 Oktober 2026 di Nusa Dua Convention Center, Bali. Sampai jumpa di lokasi!"
                : "The event runs October 2-4, 2026 at Nusa Dua Convention Center, Bali. See you there!"}
            </p>
          )}

          {status === "ended" && (
            <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base max-w-md px-2">
              {isID
                ? "Terima kasih atas partisipasi Anda di KONASPI XII. Rekaman dan dokumentasi acara akan segera tersedia."
                : "Thank you for participating in KONASPI XII. Event recordings and documentation will be available soon."}
            </p>
          )}

          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mt-1">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 text-xs sm:text-sm font-medium">
              <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 text-blue-600 dark:text-blue-400" />
              {isID ? "2-4 Oktober 2026" : "October 2-4, 2026"}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 text-xs sm:text-sm font-medium">
              <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 text-blue-600 dark:text-blue-400" />
              {isID ? "NDCC, Nusa Dua, Bali" : "NDCC, Nusa Dua, Bali"}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 text-xs sm:text-sm font-medium">
              <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 text-blue-600 dark:text-blue-400" />
              {isID ? "Konvensi & Konferensi" : "Convention & Conference"}
            </span>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2.5 sm:gap-3 w-full max-w-xs sm:max-w-none mt-2">
            {quickLinks.map((link) => {
              const Icon = link.icon
              return (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault()
                    handleLinkClick(link.href)
                  }}
                  className="group cursor-pointer inline-flex items-center justify-center gap-2 min-h-11 px-4 py-2.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100 text-sm font-semibold shadow-sm hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-md transition-all"
                >
                  <Icon className="w-4 h-4 shrink-0 text-blue-600 dark:text-blue-400" />
                  <span>{link.label}</span>
                  <ArrowRight className="w-3.5 h-3.5 shrink-0 text-gray-400 transition-transform duration-200 group-hover:translate-x-0.5" />
                </a>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}
